import { useEffect, useMemo } from 'react'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { Button, IconCloseDefault } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { useStaticData } from '@mc/static-data-react'
import { Course } from 'types/data'
import { formatChapterDuration, getEpisodeListFromCourse } from '../../../utils'
import styles from './LessonPlanPopover.module.scss'

type LessonPlanPopoverProps = {
  isOpen: boolean
  onClose: () => void
}

export const LessonPlanPopover = ({
  isOpen,
  onClose,
}: LessonPlanPopoverProps) => {
  const { t } = useTranslation('@mc/next')
  const { course } = useStaticData<{ course: Course }>()

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const episodeList = useMemo(
    () => (course ? getEpisodeListFromCourse(course) : []),
    [course],
  )

  if (!course) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className='position-fixed d-flex align-items-center justify-content-center'
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1050,
          }}
        >
          <button
            type='button'
            onClick={onClose}
            className='position-absolute'
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'transparent',
              cursor: 'default',
            }}
            aria-label={t('course.sections.skillsYoullLearn.closeLessonPlan')}
          />

          <motion.div
            initial={{ y: '100vh' }}
            animate={{ y: 0 }}
            exit={{ y: '100vh' }}
            transition={{
              type: 'spring',
              mass: 1,
              stiffness: 100,
              damping: 15,
            }}
            className={`mc-corners--md mc-bg-container-tint-dark ${styles.popover}`}
            style={{
              maxHeight: '80vh',
            }}
            onClick={(e) => e.stopPropagation()}
            role='dialog'
            aria-modal='true'
            aria-labelledby='modal-title'
          >
            {/* Header */}
            <div
              className='d-flex justify-content-between align-items-center mc-p-6'
              style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <h3 className='mc-text-h4' style={{ fontSize: 24 }}>
                {t('course.sections.skillsYoullLearn.chapters')}
              </h3>
              <Button
                onClick={onClose}
                kind='secondary'
                aria-label={t(
                  'course.sections.skillsYoullLearn.closeLessonPlan',
                )}
                className='mc-p-3 d-flex align-items-center justify-content-center mc-corners--circle'
              >
                <IconCloseDefault style={{ width: 16, height: 16 }} />
              </Button>
            </div>

            {/* Content */}
            <div
              className='mc-px-6 mc-pt-6'
              style={{ maxHeight: 'calc(80vh - 90px)', overflowY: 'auto' }}
            >
              {episodeList?.length ? (
                <>
                  {episodeList.map((chapter, index) => (
                    <ChapterItem
                      key={chapter.id || index}
                      chapter={chapter}
                      index={chapter.number || index + 1}
                    />
                  ))}
                </>
              ) : (
                <p className='mc-py-8'>
                  {t('course.sections.skillsYoullLearn.noChapters')}
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

type ChapterItemProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chapter: any // Chapter types are not accurate
  index: number
}

const ChapterItem = ({ chapter, index }: ChapterItemProps) => (
  <div className='d-flex align-items-center mc-mb-6 flex-gap-4'>
    {/* Chapter Number */}
    <div className='mc-text-h7 mc-text-color--tint'>{index}</div>

    {/* Chapter Thumbnail */}
    {chapter.imageUrl || chapter.videoThumbUrl ? (
      <Image
        className='mc-corners--sm'
        src={chapter.imageUrl || chapter.videoThumbUrl}
        alt=''
        width={35}
        height={35}
        style={{ objectFit: 'cover' }}
      />
    ) : (
      <div
        className='mc-corners--sm mc-bg-neutral-700'
        style={{ width: 35, height: 35 }}
      />
    )}

    {/* Chapter Title */}
    <div className='flex-1'>
      <h4 className='mc-text-h7 mc-text--2-lines-max' title={chapter.title}>
        {chapter.title}
      </h4>
    </div>

    {/* Chapter Duration */}
    <div className='mc-text-h7 mc-text-color--tint'>
      {formatChapterDuration(chapter.startTime, chapter.endTime)}
    </div>
  </div>
)
